/** Auth context — cookie-based on web, token-based on native */
import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';
import { router as expoRouter } from 'expo-router';
import { api, saveToken, saveRefreshToken, clearToken, loadToken, getToken, getRefreshToken, onSessionExpired } from '../api/client';
import { clearPermissionCache } from '../hooks/useAdminPermissions';
import { registerPush, unregisterPush } from '../push/registerPush';

const IS_WEB = Platform.OS === 'web';

interface User {
  id: string;
  email: string;
  name?: string;
  businessName?: string;
  phone?: string;
  accountNumber?: string;
  adminRole?: string;
  isSuspended?: boolean;
}

interface AuthState {
  user: User | null;
  role: 'owner' | 'user' | 'admin' | null;
  isSuspended: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  isLoggingOut: boolean;
  stripePublishableKey: string | null;
}

interface AuthResponse {
  requiresVerification?: boolean;
  /** False when a customer is soft-gated (authenticated but email not yet verified). */
  emailVerified?: boolean;
  requires2FA?: boolean;
  email?: string;
  token?: string;
  refreshToken?: string;
  role?: 'owner' | 'user' | 'admin';
  profile?: User;
  owner?: User;
  user?: User;
}

/** Fetch the correct Stripe publishable key from backend */
async function fetchStripeKey(): Promise<string | null> {
  try {
    const res = await api<{ publishableKey: string }>('/api/config/stripe-key');
    return res.publishableKey || null;
  } catch {
    return null;
  }
}

interface AuthContextType extends AuthState {
  login: (identifier: string, password: string) => Promise<AuthResponse>;
  loginOwner: (email: string, password: string) => Promise<AuthResponse>;
  loginUser: (email: string, password: string) => Promise<AuthResponse>;
  registerOwner: (data: { email: string; password: string; businessName: string; phone?: string; referralCode?: string; termsAccepted: true }) => Promise<AuthResponse>;
  registerUser: (data: { email: string; password: string; name: string; phone?: string; termsAccepted: true; fleetInviteToken?: string; signupVoucherCode?: string; smsMarketingOptIn?: boolean }) => Promise<AuthResponse>;
  completeVerification: (token: string, refreshToken: string, role: 'owner' | 'user' | 'admin', profile: User) => Promise<void>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

/** useAuth - access auth state, login, register, logout, and verification methods. */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/** On web, check auth via /api/auth/me (cookie-based) */
async function checkWebAuth(): Promise<{ user: User; role: 'owner' | 'user' | 'admin'; isSuspended: boolean } | null> {
  try {
    const me = await api<{ id: string; email: string; role: 'owner' | 'user' | 'admin'; isSuspended?: boolean }>('/api/auth/me');
    // Get full profile
    const profile = await api<User & { role: string }>('/api/auth/profile');
    return { user: profile, role: me.role, isSuspended: !!me.isSuspended };
  } catch { /* auth cookie missing or expired — not logged in */
    return null;
  }
}

/** AuthProvider - manages authentication state, token refresh, and role-based routing. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    role: null,
    isSuspended: false,
    isLoading: true,
    isAuthenticated: false,
    isLoggingOut: false,
    stripePublishableKey: null,
  });
  const isAuthenticatedRef = useRef(state.isAuthenticated);
  isAuthenticatedRef.current = state.isAuthenticated;

  // When the API client declares the session dead (refresh token rejected by
  // the server), clear auth state so navigation guards show the login/welcome
  // flow instead of bouncing back into authenticated screens and firing a
  // storm of 401ing fetches.  This mirrors the state-clearing part of
  // logout() WITHOUT calling the logout API (the session is already gone).
  // The client has already cleared tokens and (on native) redirected to
  // '/?expired=1'; on web we perform that navigation here, gated on having
  // actually been signed in so a stale cookie on a public page can't cause
  // a redirect loop.
  useEffect(() => {
    return onSessionExpired(() => {
      if (!isAuthenticatedRef.current) return;
      clearPermissionCache();
      setState({ user: null, role: null, isSuspended: false, isLoading: false, isAuthenticated: false, isLoggingOut: false, stripePublishableKey: null });
      if (IS_WEB && typeof window !== 'undefined' && window.location) {
        window.location.href = '/?expired=1';
      }
    });
  }, []);

  // When the app returns to the foreground (phone unlocked, app re-opened
  // after being backgrounded), the access cookie/JWT may have expired while
  // we were suspended.  Fire a no-op authenticated probe that goes through
  // api(), which transparently refreshes the session before the request.
  // Without this, the first user-initiated request post-resume often 401s
  // and (especially on native) used to log the user straight out.
  useEffect(() => {
    let lastState: AppStateStatus = AppState.currentState;
    const sub = AppState.addEventListener('change', (next) => {
      const cameToForeground =
        (lastState === 'background' || lastState === 'inactive') && next === 'active';
      lastState = next;
      if (!cameToForeground) return;
      if (!isAuthenticatedRef.current) return;
      // Web uses /me (cookie probe).  Native uses /profile (Bearer probe).
      // Either path triggers the client's proactive refresh before the request.
      const probe = IS_WEB ? '/api/auth/me' : '/api/auth/profile';
      api(probe).catch(() => { /* client handled logout if session is truly dead */ });
    });
    return () => sub.remove();
  }, []);

  // Web analytics: attach (or clear) authenticated identity on the tracking
  // beacon so product-usage pageviews are tied to the logged-in user/role.
  useEffect(() => {
    if (!IS_WEB || typeof window === 'undefined') return;
    const rpIdentify = (window as unknown as { rpIdentify?: (i: Record<string, string | undefined>) => void }).rpIdentify;
    if (typeof rpIdentify !== 'function') return;
    if (state.isAuthenticated && state.user) {
      rpIdentify({
        userId: state.user.id,
        role: state.role || undefined,
        ownerId: state.role === 'owner' ? state.user.id : undefined,
      });
    } else {
      rpIdentify({});
    }
  }, [state.isAuthenticated, state.user, state.role]);

  useEffect(() => {
    if (IS_WEB) {
      // Web: check auth status via cookies
      checkWebAuth().then(async (result) => {
        if (result) {
          const stripeKey = await fetchStripeKey();
          setState({ user: result.user, role: result.role, isSuspended: result.isSuspended, isLoading: false, isAuthenticated: true, isLoggingOut: false, stripePublishableKey: stripeKey });
        } else {
          setState({ user: null, role: null, isSuspended: false, isLoading: false, isAuthenticated: false, isLoggingOut: false, stripePublishableKey: null });
        }
      });
      return;
    }

    // Native: load tokens from secure store
    loadToken().then(async () => {
      const token = getToken();
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.exp && payload.exp * 1000 < Date.now()) {
            const rt = getRefreshToken();
            if (!rt) {
              clearToken();
              setState({ user: null, role: null, isSuspended: false, isLoading: false, isAuthenticated: false, isLoggingOut: false, stripePublishableKey: null });
              return;
            }
          }
          const stripeKey = await fetchStripeKey();
          setState({
            user: { id: payload.id, email: payload.email },
            role: payload.role,
            isSuspended: !!payload.isSuspended,
            isLoading: false,
            isAuthenticated: true,
            isLoggingOut: false,
            stripePublishableKey: stripeKey,
          });
        } catch { /* token decode/profile fetch failed — clear session */
          clearToken();
          setState({ user: null, role: null, isSuspended: false, isLoading: false, isAuthenticated: false, isLoggingOut: false, stripePublishableKey: null });
        }
      } else {
        const rt = getRefreshToken();
        if (rt) {
          api('/api/auth/profile').then(async (profile) => {
            const stripeKey = await fetchStripeKey();
            setState({
              user: profile as User,
              role: (profile as User & { role: string }).role as 'owner' | 'user' | 'admin',
              isSuspended: !!(profile as User).isSuspended,
              isLoading: false,
              isAuthenticated: true,
              isLoggingOut: false,
              stripePublishableKey: stripeKey,
            });
          }).catch(() => {
            clearToken();
            setState({ user: null, role: null, isSuspended: false, isLoading: false, isAuthenticated: false, isLoggingOut: false, stripePublishableKey: null });
          });
        } else {
          setState(s => ({ ...s, isLoading: false }));
        }
      }
    }).catch(() => {
      setState({ user: null, role: null, isSuspended: false, isLoading: false, isAuthenticated: false, isLoggingOut: false, stripePublishableKey: null });
    });
  }, []);

  // Register this device for push once a customer is authenticated (covers login,
  // register, verify, and app-boot rehydration). No-op on web and until the native
  // FCM lib + credentials ship (see src/push/registerPush.ts).
  useEffect(() => {
    if (state.isAuthenticated && state.role === 'user') {
      registerPush().catch(() => {});
    }
  }, [state.isAuthenticated, state.role]);

  /** Handle auth response — on web, tokens are in cookies; on native, save to store */
  const handleAuthSuccess = async (res: AuthResponse) => {
    if (IS_WEB) {
      // Cookies already set by backend; fetch profile for state
      const result = await checkWebAuth();
      if (result) {
        const stripeKey = await fetchStripeKey();
        setState({ user: result.user, role: result.role, isSuspended: result.isSuspended, isLoading: false, isAuthenticated: true, isLoggingOut: false, stripePublishableKey: stripeKey });
      }
    } else {
      if (res.token) await saveToken(res.token);
      if (res.refreshToken) await saveRefreshToken(res.refreshToken);
    }
  };

  const login = async (identifier: string, password: string): Promise<AuthResponse> => {
    const res = await api<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: { identifier, password },
      skipAuth: true,
    });
    if (res.requires2FA) return res;
    // Owners are hard-gated: requiresVerification with no session profile = prompt only.
    // Customers are soft-gated: the backend returns a session alongside
    // requiresVerification, so authenticate now and let them verify later.
    if (res.requiresVerification && !res.profile) return res;
    await handleAuthSuccess(res);
    if (!IS_WEB) {
      const stripeKey = await fetchStripeKey();
      setState({ user: res.profile!, role: res.role!, isSuspended: !!res.profile?.isSuspended, isLoading: false, isAuthenticated: true, isLoggingOut: false, stripePublishableKey: stripeKey });
    }
    return res;
  };

  const loginOwner = async (email: string, password: string): Promise<AuthResponse> => {
    const res = await api<AuthResponse>('/api/auth/owner/login', {
      method: 'POST',
      body: { identifier: email, password },
      skipAuth: true,
    });
    if (res.requiresVerification || res.requires2FA) return res;
    await handleAuthSuccess(res);
    if (!IS_WEB) {
      const stripeKey = await fetchStripeKey();
      setState({ user: res.owner!, role: 'owner', isSuspended: !!res.owner?.isSuspended, isLoading: false, isAuthenticated: true, isLoggingOut: false, stripePublishableKey: stripeKey });
    }
    return res;
  };

  const loginUser = async (email: string, password: string): Promise<AuthResponse> => {
    const res = await api<AuthResponse>('/api/auth/user/login', {
      method: 'POST',
      body: { identifier: email, password },
      skipAuth: true,
    });
    if (res.requires2FA) return res;
    // Customers are soft-gated: authenticate even when requiresVerification is set
    // (the backend issued a session). Returns res so the caller can prompt to verify.
    await handleAuthSuccess(res);
    if (!IS_WEB) {
      const stripeKey = await fetchStripeKey();
      setState({ user: res.user!, role: 'user', isSuspended: !!res.user?.isSuspended, isLoading: false, isAuthenticated: true, isLoggingOut: false, stripePublishableKey: stripeKey });
    }
    return res;
  };

  const registerOwner = async (data: { email: string; password: string; businessName: string; phone?: string; referralCode?: string; termsAccepted: true }): Promise<AuthResponse> => {
    const res = await api<AuthResponse>('/api/auth/owner/register', {
      method: 'POST',
      body: data,
      skipAuth: true,
    });
    if (res.requiresVerification) return res;
    await handleAuthSuccess(res);
    if (!IS_WEB) {
      const stripeKey = await fetchStripeKey();
      setState({ user: res.owner!, role: 'owner', isSuspended: !!res.owner?.isSuspended, isLoading: false, isAuthenticated: true, isLoggingOut: false, stripePublishableKey: stripeKey });
    }
    return res;
  };

  const registerUser = async (data: { email: string; password: string; name: string; phone?: string; termsAccepted: true; fleetInviteToken?: string; signupVoucherCode?: string; smsMarketingOptIn?: boolean }): Promise<AuthResponse> => {
    const res = await api<AuthResponse>('/api/auth/user/register', {
      method: 'POST',
      body: data,
      skipAuth: true,
    });
    // Customers are soft-gated: the backend issues a session at registration so a
    // new customer can pay immediately. Authenticate now; caller prompts to verify.
    await handleAuthSuccess(res);
    if (!IS_WEB) {
      const stripeKey = await fetchStripeKey();
      setState({ user: res.user!, role: 'user', isSuspended: !!res.user?.isSuspended, isLoading: false, isAuthenticated: true, isLoggingOut: false, stripePublishableKey: stripeKey });
    }
    return res;
  };

  const completeVerification = async (token: string, rt: string, role: 'owner' | 'user' | 'admin', profile: User) => {
    if (IS_WEB) {
      const stripeKey = await fetchStripeKey();
      setState({ user: profile, role, isSuspended: !!profile.isSuspended, isLoading: false, isAuthenticated: true, isLoggingOut: false, stripePublishableKey: stripeKey });
    } else {
      await saveToken(token);
      await saveRefreshToken(rt);
      const stripeKey = await fetchStripeKey();
      setState({ user: profile, role, isSuspended: !!profile.isSuspended, isLoading: false, isAuthenticated: true, isLoggingOut: false, stripePublishableKey: stripeKey });
    }
  };

  const logout = async () => {
    setState(prev => ({ ...prev, isLoggingOut: true }));
    await unregisterPush().catch(() => {});
    if (IS_WEB) {
      try {
        await api('/api/auth/logout', { method: 'POST', body: {} });
      } catch { /* best effort */ }
    } else {
      const rt = getRefreshToken();
      if (rt) {
        try {
          await api('/api/auth/logout', { method: 'POST', body: { refreshToken: rt }, skipAuth: true });
        } catch { /* best effort */ }
      }
    }
    await clearToken();
    clearPermissionCache();
    // Clear auth state first, then navigate. Layouts return null when !isAuthenticated,
    // and the navigate dismisses the stack screen to show the welcome page.
    setState({ user: null, role: null, isSuspended: false, isLoading: false, isAuthenticated: false, isLoggingOut: false, stripePublishableKey: null });
    if (IS_WEB && typeof window !== 'undefined') {
      window.location.href = '/';
    } else {
      // Use dismissAll to pop back to root, then replace to welcome screen
      try { expoRouter.dismissAll(); } catch { /* may not have screens to dismiss */ }
      try { expoRouter.replace('/'); } catch { /* non-critical */ }
    }
  };

  const logoutAll = async () => {
    setState(prev => ({ ...prev, isLoggingOut: true }));
    try {
      await api('/api/auth/logout-all', { method: 'POST' });
    } catch { /* best effort */ }
    await clearToken();
    clearPermissionCache();
    setState({ user: null, role: null, isSuspended: false, isLoading: false, isAuthenticated: false, isLoggingOut: false, stripePublishableKey: null });
    if (IS_WEB && typeof window !== 'undefined') {
      window.location.href = '/';
    } else {
      try { expoRouter.dismissAll(); } catch { /* may not have screens to dismiss */ }
      try { expoRouter.replace('/'); } catch { /* non-critical */ }
    }
  };

  return (
    <AuthContext.Provider value={{ ...state, login, loginOwner, loginUser, registerOwner, registerUser, completeVerification, logout, logoutAll }}>
      {children}
    </AuthContext.Provider>
  );
}
